import {
    Collapse,
    Radio,
    Typography,
    Paper,
    FormControlLabel,
    FormControl,
    RadioGroup,
    Button,
    Grid,
} from "@material-ui/core";
import { observer } from "mobx-react";
import moment from "moment";
import React, { useState } from "react";
import { useStore } from "store/StoreConfigs";
import { eventInfoStyles } from "./EventInfoStyles";
import { ReactComponent as PriorityFlag } from "icons/PriorityFlag.svg";
import { ReactComponent as EventChevronDown } from "icons/EventChevronDown.svg";
import { ReactComponent as EventChevronUp } from "icons/EventChevronUp.svg";
import { ReactComponent as AddDocumentPlus } from "icons/AddDocumentPlus.svg";
import { ReactComponent as DisabledPlus } from "icons/DisabledPlus.svg";
import { ReactComponent as ViewAttachedFiles } from "icons/ViewAttachedFiles.svg";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { EventPriorityRequest } from "api/thorRequestModels/Events/EventPriorityRequest";
import { EventStatus } from "models/thorEventModels/EventDetails";
import AssignedUsers from "components/AssignedUsers/AssignedUsers";
import clsx from "clsx";
import DatePicker from "react-datepicker";

export default observer(function EventInfo() {
    const { thorEventViewStore, identityStore } = useStore();

    const [isOpen, setIsOpen] = useState(false);
    const [dueDate, setDueDate] = useState("");

    const isUserHavePermission: boolean =
        identityStore.userInfo?.email === thorEventViewStore.eventDetails.reviewerUserId ||
        identityStore.userInfo?.email === thorEventViewStore.eventDetails.qaUserId;

    const classes = eventInfoStyles();

    const handlePriorityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const request: EventPriorityRequest = {
            eventId: thorEventViewStore.eventDetails.eventId,
            customerId: identityStore.currentCustomerId,
            priority: event.target.value,
        };
        thorEventViewStore.setEventPriority(request);
        setIsOpen(false);
    };

    const onDueDateChange = (update: Date) => {
        const selectedDate = moment(update).format("DD MMM YYYY");
        if (!update || dueDate === selectedDate) {
            return;
        }
        thorEventViewStore.changeDueDate({
            customerId: identityStore.currentCustomerId,
            dueDate: moment
                .utc(`${update.getFullYear()}-${update.getMonth() + 1}-${update.getDate()}`, "YYYY-MM-DD")
                .toDate(),
            eventId: thorEventViewStore.eventDetails.eventId,
        });
        setDueDate(selectedDate);
    };

    const getDueDateValue = (): string => {
        if (dueDate) {
            return dueDate;
        }
        return thorEventViewStore.eventDetails.dueDate
            ? moment(thorEventViewStore.eventDetails.dueDate).format("DD MMM YYYY")
            : "";
    };

    return (
        <>
            <Typography className={classes.subTitle + " " + classes.textUpperCase}>Event Id</Typography>

            <Grid container justifyContent="space-between">
                <Grid item>
                    <Typography className={classes.eventId}>
                        {"TE" + thorEventViewStore.eventDetails.eventId}
                    </Typography>

                    <Typography className={classes.subTitle}>
                        Event Creation Date:{" "}
                        {moment.utc(thorEventViewStore.eventDetails.eventCreatedAt).format("DD MMM YYYY  HH:mm:ss")}
                    </Typography>

                    {thorEventViewStore.eventDetails.eventStatusId === EventStatus.New ? (
                        <Typography className={classes.new}>New Event</Typography>
                    ) : thorEventViewStore.eventDetails.eventStatusId === EventStatus.Closed ? (
                        <Typography className={classes.complete}>Evaluation Complete</Typography>
                    ) : (
                        <Typography className={classes.review}>
                            {thorEventViewStore.eventDetails.eventStatusId === EventStatus.PendingReview
                                ? "Pending Review"
                                : thorEventViewStore.eventDetails.eventStatusId === EventStatus.PendingApproval
                                ? "Pending Approval"
                                : thorEventViewStore.eventDetails.eventStatusId === EventStatus.ReOpened
                                ? "Re-Opened"
                                : null}{" "}
                        </Typography>
                    )}
                </Grid>
                <Grid item>
                    <Typography className={classes.subTitleSecondVar}>Priority</Typography>
                    {thorEventViewStore.isPrioritySet ? (
                        <LoadingIndicator className={classes.smallIndicator} />
                    ) : (
                        <>
                            <Typography className={classes.iconContainer + " " + classes.alignMargin}>
                                <span className={classes.iconSpacing}>
                                    {thorEventViewStore.eventDetails.priority === "Medium" ? (
                                        <PriorityFlag style={{ fill: "#FFAB00" }} />
                                    ) : thorEventViewStore.eventDetails.priority === "High" ? (
                                        <PriorityFlag style={{ fill: "#DF5031" }} />
                                    ) : thorEventViewStore.eventDetails.priority === "Low" ? (
                                        <PriorityFlag style={{ fill: "#00AF41" }} />
                                    ) : null}
                                </span>
                                {thorEventViewStore.eventDetails.priority}{" "}
                                {thorEventViewStore.eventDetails.eventStatusId === EventStatus.Closed ||
                                !isUserHavePermission ? null : isOpen ? (
                                    <EventChevronUp
                                        style={{ cursor: "pointer", fill: "#333333" }}
                                        onClick={() => {
                                            setIsOpen(false);
                                        }}
                                    />
                                ) : (
                                    <EventChevronDown
                                        style={{ cursor: "pointer", fill: "#333333" }}
                                        onClick={() => {
                                            setIsOpen(true);
                                        }}
                                    />
                                )}
                            </Typography>
                            <Collapse in={isOpen} timeout="auto" appear={false} unmountOnExit>
                                <Paper elevation={0} className={classes.radioButtonContainer}>
                                    <FormControl component="fieldset">
                                        <RadioGroup
                                            value={thorEventViewStore.eventDetails.priority}
                                            onChange={handlePriorityChange}
                                        >
                                            <FormControlLabel
                                                value="High"
                                                className={classes.radio}
                                                control={<Radio value="High" size="small" />}
                                                label="High"
                                            />
                                            <FormControlLabel
                                                className={classes.radio}
                                                value="Medium"
                                                control={<Radio value="Medium" size="small" />}
                                                label="Medium"
                                            />
                                            <FormControlLabel
                                                className={classes.radio}
                                                value="Low"
                                                control={<Radio value="Low" size="small" />}
                                                label="Low"
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                </Paper>
                            </Collapse>
                        </>
                    )}
                    <Typography className={classes.subTitle}>Due Date:</Typography>
                    <DatePicker
                        disabled={
                            thorEventViewStore.eventDetails.eventStatusId === EventStatus.Closed ||
                            !isUserHavePermission
                        }
                        className={classes.picker}
                        selectsRange={false}
                        value={getDueDateValue()}
                        minDate={new Date(Date.now() + 3600 * 1000 * 24)}
                        onChange={(update: any) => {
                            onDueDateChange(update);
                        }}
                        placeholderText="Select due date"
                        disabledKeyboardNavigation
                    />
                </Grid>
                <Grid item>
                    <Typography className={classes.subTitleSecondVar}>Attached Documents</Typography>
                    <Typography
                        onClick={() => {
                            thorEventViewStore.setFileViewModalOpen(true);
                        }}
                        className={classes.fileIconSpace}
                    >
                        <ViewAttachedFiles className={classes.fileIcon} /> View Attached Documents
                    </Typography>
                    <Typography
                        onClick={
                            isUserHavePermission && thorEventViewStore.eventDetails.eventStatusId !== EventStatus.Closed
                                ? () => {
                                      thorEventViewStore.setFileAttachModalOpen(true);
                                  }
                                : () => {}
                        }
                        className={clsx({
                            [classes.fileIconSpace]: isUserHavePermission,
                            [classes.disabledField]:
                                !isUserHavePermission ||
                                thorEventViewStore.eventDetails.eventStatusId === EventStatus.Closed,
                        })}
                    >
                        {isUserHavePermission &&
                        thorEventViewStore.eventDetails.eventStatusId !== EventStatus.Closed ? (
                            <AddDocumentPlus className={classes.fileIcon} />
                        ) : (
                            <DisabledPlus className={classes.fileIcon} />
                        )}{" "}
                        Add New Document
                    </Typography>
                </Grid>
                <Grid item>
                    {!thorEventViewStore.eventDetails.reviewerUserId ? (
                        <Button
                            className={classes.assignButton}
                            onClick={() => {
                                thorEventViewStore.setAssignMenuOpen(true);
                            }}
                        >
                            <Typography>Assign Event</Typography>
                        </Button>
                    ) : (
                        <AssignedUsers />
                    )}
                    {thorEventViewStore.eventDetails.eventStatusId === EventStatus.Closed && (
                        <>
                            <br />
                            <Button
                                className={classes.reopenButton}
                                onClick={() => {
                                    thorEventViewStore.setSelectedBatch(0);
                                    thorEventViewStore.reopenEvent({
                                        eventId: thorEventViewStore.eventDetails.eventId,
                                        customerId: identityStore.currentCustomerId,
                                        userEmail: identityStore.userInfo!.email,
                                    });
                                }}
                            >
                                <Typography>Reopen Event</Typography>
                            </Button>
                        </>
                    )}
                </Grid>
            </Grid>
        </>
    );
});
